import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, useTheme, alpha, Dialog, DialogTitle, DialogContent, Tooltip, Divider, useMediaQuery } from '@mui/material';
import { Icon } from '@iconify/react';
import { CloseCircle, Eye } from 'iconsax-react';
import { PdfIcon } from '@ui/assets/custom-icons/pdf-icon';
import { useTranslation } from 'react-i18next';
const UndertakingField = ({ id, label, required, value, uploadedFileData, onChange, onRemove, disabled = false, readonly = false, error = false, helperText = '', languages = [], onLanguageFilesChange, filesByLanguage, languageErrors, uploadedByLanguage }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    // State to track files for each language
    const [languageFiles, setLanguageFiles] = useState({});
    const [languageUploadedData, setLanguageUploadedData] = useState({});
    // Track if we've initialized to prevent re-initializing when props change after user actions
    const initializedRef = useRef(false);
    // Initialize with existing server-provided uploaded files by language (only once on mount)
    useEffect(() => {
        // Only initialize once on mount, don't re-initialize when props change after user removes files
        if (initializedRef.current)
            return;
        // Server provided per-language uploads
        if (uploadedByLanguage && Object.keys(uploadedByLanguage).length > 0) {
            // Deep clone to avoid shared references across languages
            const cloned = Object.entries(uploadedByLanguage).reduce((acc, [langId, arr]) => {
                acc[langId] = arr.map((item) => ({ ...item }));
                return acc;
            }, {});
            setLanguageUploadedData(cloned);
            initializedRef.current = true;
            return;
        }
        // Backward compatibility (legacy single file from server)
        if (uploadedFileData) {
            // Assign legacy single server file to the first selected language only
            const firstLangId = (languages && languages[0] && languages[0].id) || 'en';
            setLanguageUploadedData({ [firstLangId]: [uploadedFileData] });
            initializedRef.current = true;
        }
        else if (value) {
            // Legacy local value: assign to first language to make it visible
            const firstLangId = (languages && languages[0] && languages[0].id) || 'en';
            setLanguageFiles({ [firstLangId]: [value] });
            initializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedByLanguage, value, uploadedFileData, languages]);
    const fileInputRefs = useRef({});
    const [dragOverLanguage, setDragOverLanguage] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLanguage, setPreviewLanguage] = useState('');
    const notifyChange = (next) => {
        onLanguageFilesChange?.(next);
    };
    const [fileValidationErrors, setFileValidationErrors] = useState({});
    const handleFileSelect = (file, languageId) => {
        const maxSize = 10 * 1024 * 1024; // 10MB
        const errors = { ...fileValidationErrors };
        // Validate file type
        if (file.type !== 'application/pdf') {
            errors[languageId] =
                t('fileUpload.fileTypeError', { field: 'PDF' }) || 'Only PDF files are allowed.';
            setFileValidationErrors(errors);
            return;
        }
        // Validate file size
        if (file.size > maxSize) {
            errors[languageId] =
                t('validations.fileSizeTooLarge', { fileNumber: 1, maxSize: '10MB' }) ||
                    'File size must be less than 10MB.';
            setFileValidationErrors(errors);
            return;
        }
        // Clear error for this language if validation passes
        delete errors[languageId];
        setFileValidationErrors(errors);
        // Update language-specific file (single file per language)
        setLanguageFiles((prev) => {
            const next = { ...prev, [languageId]: [file] };
            notifyChange(next);
            return next;
        });
        // For backward compatibility, if it's English, also call onChange
        if (languageId === 'en') {
            onChange?.(file);
        }
    };
    const handlePreview = (languageId, index = 0) => {
        const files = languageFiles[languageId] || [];
        const file = files[index];
        const uploadedDataArr = languageUploadedData[languageId] || [];
        const uploadedData = uploadedDataArr[index];
        if (file) {
            // For newly uploaded files: open preview dialog
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setPreviewLanguage(languageId);
            setPreviewOpen(true);
        }
        else if (uploadedData) {
            // For existing files from backend: directly download the file
            const link = document.createElement('a');
            link.href = uploadedData.url;
            link.download = uploadedData.name;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };
    const handleClosePreview = () => {
        setPreviewOpen(false);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setPreviewLanguage('');
    };
    const handleFileInputChange = (event, languageId) => {
        const file = (event.target.files && event.target.files[0]) || null;
        if (file)
            handleFileSelect(file, languageId);
        // Reset the input value to allow selecting the same file again
        event.target.value = '';
    };
    const handleDrop = (event, languageId) => {
        event.preventDefault();
        setDragOverLanguage(null);
        if (disabled || readonly)
            return;
        const file = (event.dataTransfer.files && event.dataTransfer.files[0]) || null;
        if (file)
            handleFileSelect(file, languageId);
        // Clear the data transfer to allow dropping the same file again
        event.dataTransfer.clearData();
    };
    const handleDragOver = (event, languageId) => {
        event.preventDefault();
        // Allow drag over if not disabled/readonly (even if file exists, to allow replacement)
        if (!disabled && !readonly) {
            setDragOverLanguage(languageId);
        }
    };
    const handleDragLeave = () => {
        setDragOverLanguage(null);
    };
    const handleUploadClick = (languageId) => {
        if (!disabled && !readonly) {
            fileInputRefs.current[languageId]?.click();
        }
    };
    const handleRemoveFile = (languageId, index) => {
        if (!disabled && !readonly) {
            // Remove from newly selected files
            setLanguageFiles((prev) => {
                const nextArr = [...(prev[languageId] || [])];
                nextArr.splice(index, 1);
                const next = { ...prev, [languageId]: nextArr };
                notifyChange(next);
                return next;
            });
            // Clear uploaded data for this language to allow new file upload
            setLanguageUploadedData((prev) => {
                const next = { ...prev };
                delete next[languageId];
                return next;
            });
            // Clear validation errors for this language
            setFileValidationErrors((prev) => {
                const next = { ...prev };
                delete next[languageId];
                return next;
            });
            // Reset the file input value
            if (fileInputRefs.current[languageId]) {
                fileInputRefs.current[languageId].value = '';
            }
        }
    };
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, children: languages.map((language) => {
                    const langId = language.id;
                    const langFiles = languageFiles[langId] || [];
                    const langUploaded = languageUploadedData[langId] || [];
                    const hasFiles = langFiles.length > 0 || langUploaded.length > 0;
                    const isDragOver = dragOverLanguage === langId;
                    const langError = languageErrors?.[langId] || fileValidationErrors[langId];
                    return (_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'stretch',
                            padding: 1,
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                            borderRadius: '14px',
                            backgroundColor: 'transparent',
                            gap: 1,
                            flexDirection: isSmallScreen ? 'column' : 'row'
                        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, minWidth: 60 }, children: [_jsx(Box, { component: 'img', src: language.flagUrl || '/assets/images/flags/empty-flag.svg', alt: language.name, sx: {
                                            width: 24,
                                            height: 18,
                                            objectFit: 'cover',
                                            borderRadius: 0.5
                                        } }), _jsx(Typography, { variant: 'body2', fontWeight: 400, children: language.name })] }), _jsx(Divider, { orientation: 'vertical', flexItem: true, sx: {
                                    borderColor: theme.palette.grey[400],
                                    borderWidth: '1px',
                                    alignSelf: 'stretch'
                                } }), _jsxs(Box, { sx: { flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }, children: [!hasFiles ? (_jsx(Box, { sx: {
                                            width: '100%',
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                            cursor: disabled || readonly ? 'not-allowed' : 'pointer',
                                            backgroundColor: isDragOver
                                                ? alpha(theme.palette.primary.main, 0.05)
                                                : langError
                                                    ? alpha(theme.palette.error.main, 0.08)
                                                    : theme.palette.common.grey8,
                                            border: isDragOver
                                                ? `1px dashed ${theme.palette.primary.main}`
                                                : langError
                                                    ? `1px dashed ${theme.palette.error.main}`
                                                    : `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
                                            transition: 'all 0.2s ease-in-out'
                                        }, onDrop: (e) => handleDrop(e, langId), onDragOver: (e) => handleDragOver(e, langId), onDragLeave: handleDragLeave, onClick: () => handleUploadClick(langId), children: _jsx(Box, { sx: {
                                                padding: 2.5,
                                                textAlign: 'center'
                                            }, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center', alignItems: 'center', children: _jsx(Typography, { variant: 'body2', sx: {
                                                        color: disabled || readonly
                                                            ? theme.palette.grey[400]
                                                            : theme.palette.text.disabled,
                                                        fontWeight: 400
                                                    }, children: t('fileUpload.uploadFile') }) }) }) })) : (_jsxs(Box, { sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            position: 'relative'
                                        }, onDrop: (e) => handleDrop(e, langId), onDragOver: (e) => handleDragOver(e, langId), onDragLeave: handleDragLeave, children: [langFiles[0] && (_jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: 1,
                                                    borderRadius: 1,
                                                    border: isDragOver
                                                        ? `2px dashed ${theme.palette.primary.main}`
                                                        : `1px solid ${theme.palette.grey[300]}`,
                                                    backgroundColor: isDragOver
                                                        ? alpha(theme.palette.primary.main, 0.05)
                                                        : theme.palette.common.white,
                                                    transition: 'all 0.2s ease-in-out'
                                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(PdfIcon, { width: '22px', height: '22px' }), _jsx(Typography, { variant: 'body2', fontWeight: 400, color: theme.palette.text.primary, children: langFiles[0].name })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Tooltip, { title: 'Preview PDF', children: _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        handlePreview(langId, 0);
                                                                    }, sx: { border: 'none', padding: 0.5 }, children: _jsx(Eye, { variant: 'Outline', width: 18, height: 18 }) }) }), _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveFile(langId, 0);
                                                                }, disabled: disabled || readonly, sx: { border: 'none', padding: 0.5 }, children: _jsx(Icon, { icon: 'eva:close-fill', width: '18', height: '18' }) })] })] }, `${langId}-new-${langFiles[0].name}`)), !langFiles[0] && langUploaded[0] && (_jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: 1,
                                                    borderRadius: 1,
                                                    border: isDragOver
                                                        ? `2px dashed ${theme.palette.primary.main}`
                                                        : `1px solid ${theme.palette.grey[300]}`,
                                                    backgroundColor: isDragOver
                                                        ? alpha(theme.palette.primary.main, 0.05)
                                                        : theme.palette.common.white,
                                                    transition: 'all 0.2s ease-in-out'
                                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(PdfIcon, { width: '22px', height: '22px' }), _jsx(Typography, { variant: 'body2', fontWeight: 400, color: theme.palette.text.primary, children: langUploaded[0].name })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Tooltip, { title: 'Preview PDF', children: _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        handlePreview(langId, 0);
                                                                    }, sx: { border: 'none', padding: 0.5 }, children: _jsx(Eye, { variant: 'Outline', width: 18, height: 18 }) }) }), _jsx(IconButton, { size: 'small', color: 'default', onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveFile(langId, 0);
                                                                }, disabled: disabled || readonly, sx: { border: 'none', padding: 0.5 }, children: _jsx(Icon, { icon: 'eva:close-fill', width: '18', height: '18' }) })] })] }, `${langId}-uploaded-${langUploaded[0].id}`))] })), langError && (_jsx(Typography, { variant: 'caption', color: 'error', sx: {
                                            display: 'block',
                                            marginLeft: 1
                                        }, children: langError }))] }), _jsx("input", { ref: (el) => {
                                    fileInputRefs.current[langId] = el;
                                }, id: `undertaking-input-${id}-${langId}`, type: 'file', accept: '.pdf,application/pdf', onChange: (e) => handleFileInputChange(e, langId), style: { display: 'none' }, disabled: disabled || readonly })] }, langId));
                }) }), _jsxs(Dialog, { open: previewOpen, onClose: handleClosePreview, maxWidth: 'lg', fullWidth: true, PaperProps: {
                    sx: {
                        height: '90vh',
                        maxHeight: '90vh'
                    }
                }, children: [_jsx(DialogTitle, { children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }, children: [_jsxs(Typography, { variant: 'h6', children: [t('common.pdfPreview'), " - ", languages.find((l) => l.id === previewLanguage)?.name] }), _jsx(IconButton, { onClick: handleClosePreview, size: 'small', children: _jsx(CloseCircle, { size: 20 }) })] }) }), _jsx(DialogContent, { sx: { padding: 0, height: '100%' }, children: previewUrl && (_jsx(Box, { component: 'iframe', src: previewUrl, sx: {
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                minHeight: '70vh'
                            } })) })] }), error && helperText && (_jsx(Typography, { variant: 'caption', color: 'error', sx: {
                    display: 'block',
                    marginTop: 1,
                    marginLeft: 1.5
                }, children: helperText }))] }));
};
export default UndertakingField;
